import {
  AGENCY_ACCOUNT_LINKING_SHEET_APPROVAL_BODY,
  AGENCY_ACCOUNT_LINKING_SHEET_APPROVAL_TITLE,
  AGENCY_ACCOUNT_LINKING_SHEET_APPROVE_GENERIC,
  AGENCY_ACCOUNT_LINKING_SHEET_APPROVE_GOOGLE,
  AGENCY_ACCOUNT_LINKING_SHEET_APPROVE_META,
  AGENCY_ACCOUNT_LINKING_SHEET_APPROVE_TIKTOK,
  AGENCY_ACCOUNT_LINKING_SHEET_FAILED_BODY,
  AGENCY_ACCOUNT_LINKING_SHEET_FAILED_TITLE,
  AGENCY_ACCOUNT_LINKING_SHEET_IN_PROGRESS,
  AGENCY_ACCOUNT_LINKING_SHEET_IN_PROGRESS_TITLE,
  AGENCY_ACCOUNT_LINKING_SHEET_NOT_CONFIGURED,
  AGENCY_ACCOUNT_LINKING_SHEET_PARTIAL_BODY,
  AGENCY_ACCOUNT_LINKING_SHEET_PARTIAL_TITLE,
  AGENCY_ACCOUNT_LINKING_SHEET_PROCESSING,
  AGENCY_ACCOUNT_LINKING_SHEET_PROCESSING_TITLE,
  AGENCY_ACCOUNT_LINKING_SHEET_RECONNECT,
  AGENCY_ACCOUNT_LINKING_SHEET_REQUEST_AGAIN,
  AGENCY_ACCOUNT_LINKING_SHEET_RETRY_STEP,
  AGENCY_ACCOUNT_LINKING_SHEET_SUPPORT,
  AGENCY_ACCOUNT_LINKING_TOAST_LATER,
  AGENCY_ACCOUNT_LINKING_STATUS_UNLINKED,
  AGENCY_ACCOUNT_LINKING_STATUS_PENDING,
  AGENCY_ACCOUNT_LINKING_STATUS_FAILED,
  AGENCY_ACCOUNT_LINKING_STATUS_LINKED,
  AGENCY_ACCOUNT_LINKING_DETAIL_NOT_ADMIN,
  AGENCY_ACCOUNT_LINKING_DETAIL_RECONNECT,
  AGENCY_ACCOUNT_LINKING_DETAIL_UNREADABLE,
  AGENCY_ACCOUNT_LINKING_DETAIL_NOT_CONFIGURED,
  AGENCY_ACCOUNT_LINKING_DETAIL_NOT_IN_BUSINESS,
  AGENCY_ACCOUNT_LINKING_DETAIL_LIMIT,
  AGENCY_ACCOUNT_LINKING_DETAIL_MANAGED_ELSEWHERE,
  AGENCY_ACCOUNT_LINKING_DETAIL_INCOMPATIBLE,
  AGENCY_ACCOUNT_LINKING_DETAIL_REJECTED,
  AGENCY_ACCOUNT_LINKING_DETAIL_EXPIRED,
  AGENCY_ACCOUNT_LINKING_DETAIL_REVOKED,
  AGENCY_ACCOUNT_LINKING_DETAIL_UNSUPPORTED,
  AGENCY_ACCOUNT_LINKING_DETAIL_GENERIC,
} from "../i18n/keysTranslations.js";

export const AGENCY_LINKING_PLATFORM = Object.freeze({
  META: "meta",
  GOOGLE: "google",
  TIKTOK: "tiktok",
});

export const AGENCY_LINKING_STATUS = Object.freeze({
  UNLINKED: "unlinked",
  PENDING: "pending",
  LINKED: "linked",
  FAILED: "failed",
});

export const AGENCY_LINKING_STAGE = Object.freeze({
  QUEUED: "queued",
  LINKING: "linking",
  AWAITING_CLIENT_APPROVAL: "awaitingClientApproval",
  AWAITING_PARTNER: "awaitingPartner",
});

export const AGENCY_LINKING_SOURCE = Object.freeze({
  CONNECTION_PICKER: "connectionPicker",
  DASHBOARD_CARD: "dashboardCard",
  AGENCY_SHEET: "agencySheet",
});

export const AGENCY_LINKING_VARIANT = Object.freeze({
  DIRECT: "direct",
  AGENCY: "agency",
});

export const AGENCY_LINKING_CODE = Object.freeze({
  IN_PROGRESS: "agencyLinkInProgress",
  CONSENT_REQUIRES_CLIENT: "agencyLinkConsentRequiresClient",
  CONSENT_REQUIRED: "agencyLinkConsentRequired",
  NOT_CONFIGURED: "agencyLinkingNotConfigured",
});

// OAuth return query. The value is the batch id (`resumeBatchID`).
export const AGENCY_LINK_RESUME_QUERY = "agencyLinkResume";

export const AGENCY_LINKING_TOAST_TONE = Object.freeze({
  SUCCESS: "success",
  NEUTRAL: "neutral",
});

export const AGENCY_LINKING_COPY_VERSION = "2148-v1";

const PICKER_SHOWN_PREFIX = "agencyAccountLinking:pickerShown:";
const PICKER_DEDUPE_MS = 60 * 1000;
const LOCALES = new Set(["es", "en", "pt"]);

export const toAgencyLinkingPlatform = (platform) => {
  const value = String(platform || "")
    .toLowerCase()
    .replace(/[\s_-]/g, "");

  if (value === "meta" || value === "facebook") {
    return AGENCY_LINKING_PLATFORM.META;
  }
  if (value === "google" || value === "googleads") {
    return AGENCY_LINKING_PLATFORM.GOOGLE;
  }
  if (value === "tiktok" || value === "tiktokbusiness") {
    return AGENCY_LINKING_PLATFORM.TIKTOK;
  }
  return null;
};

export const toAgencyLinkingLocale = (language) => {
  const value = String(language || "")
    .toLowerCase()
    .split("-")[0];
  return LOCALES.has(value) ? value : "es";
};

export const isAgencyLinkingPlatformAvailable = (
  platform,
  availablePlatforms = []
) => {
  const normalized = toAgencyLinkingPlatform(platform);
  if (!normalized) return false;
  return availablePlatforms.some(
    (item) => toAgencyLinkingPlatform(item) === normalized
  );
};

// The backend rejects the whole link request when any account's platform is
// not in availablePlatforms, so there is no fallback platform.
export const platformWireValue = (platform, availablePlatforms = []) =>
  isAgencyLinkingPlatformAvailable(platform, availablePlatforms)
    ? toAgencyLinkingPlatform(platform)
    : null;

export const filterAccountsByAvailablePlatforms = ({
  accounts = [],
  availablePlatforms = [],
} = {}) =>
  accounts.filter((account) =>
    isAgencyLinkingPlatformAvailable(account?.platform, availablePlatforms)
  );

export const truncateAccountId = (accountID) => {
  const value = String(accountID || "").trim();
  if (!value) return "";
  if (value.length <= 10) return value;
  return `${value.slice(0, 8)}...`;
};

const readObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : null;

const unwrapPayload = (raw) => {
  const root = readObject(raw) || {};
  const data = readObject(root.data);
  return { root, data: data || {} };
};

const toEntries = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  const record = readObject(value);
  if (!record) return [];
  return Object.entries(record).map(([key, account]) => {
    if (account && typeof account === "object") {
      return { ...account, id: account.id || key };
    }
    return { id: key, name: account };
  });
};

const readAccountName = (account) =>
  account?.adAccountName ||
  account?.name ||
  account?.accountName ||
  account?.descriptiveName ||
  account?.advertiserName ||
  "";

const readAccountID = (account, fallback) =>
  String(
    account?.adAccountID ||
      account?.accountID ||
      account?.adAccountId ||
      account?.accountId ||
      account?.advertiserID ||
      account?.id ||
      fallback ||
      ""
  );

const normalizeStatus = (status) => {
  const value = String(status || "")
    .toLowerCase()
    .replace(/[\s-]/g, "_");
  if (value === AGENCY_LINKING_STATUS.LINKED) {
    return AGENCY_LINKING_STATUS.LINKED;
  }
  if (value === AGENCY_LINKING_STATUS.FAILED) {
    return AGENCY_LINKING_STATUS.FAILED;
  }
  if (value === AGENCY_LINKING_STATUS.PENDING) {
    return AGENCY_LINKING_STATUS.PENDING;
  }
  if (value === AGENCY_LINKING_STATUS.UNLINKED) {
    return AGENCY_LINKING_STATUS.UNLINKED;
  }
  return null;
};

// Linked writes `stage: ""`. Failed omits stage. Both mean "no stage".
export const normalizeAgencyLinkStage = (stage) => {
  if (stage == null || stage === "") return null;
  const value = String(stage);
  if (Object.values(AGENCY_LINKING_STAGE).includes(value)) return value;
  return null;
};

// RTDB link doc key: one node per platform ad account.
export const agencyAccountLinkKey = ({ platform, adAccountID }) =>
  `${platform}:${adAccountID}`;

// UI row key: same ad account via different integrations is a separate row.
export const agencyAccountSelectionKey = ({
  platform,
  adAccountID,
  integrationID,
}) => `${platform}:${adAccountID}:${integrationID}`;

export const agencyAccountLinkPath = ({ platform, adAccountID }) =>
  `agencyAccountLinks/${platform}/${adAccountID}`;

export const agencyLinkBatchPath = ({ batchID }) =>
  `agencyLinkBatches/${batchID}`;

const normalizeAccount = (account, index) => {
  const platform = toAgencyLinkingPlatform(account?.platform);
  const accountID = readAccountID(account, index);
  const integrationID = String(
    account?.integrationID || account?.integrationId || ""
  );
  if (!platform || !accountID) return null;

  const name = readAccountName(account) || accountID;
  const advertiserID = account?.advertiserID
    ? String(account.advertiserID)
    : null;

  const linkKey = agencyAccountLinkKey({
    platform,
    adAccountID: accountID,
  });
  const id = integrationID
    ? agencyAccountSelectionKey({
        platform,
        adAccountID: accountID,
        integrationID,
      })
    : linkKey;

  return {
    id,
    linkKey,
    advertiserID,
    accountID,
    adAccountID: accountID,
    integrationID,
    integrationName: String(account?.integrationName || "") || integrationID,
    platform,
    name,
    status: normalizeStatus(account?.status),
    stage: normalizeAgencyLinkStage(account?.stage),
    errorCode: account?.errorCode || null,
  };
};

export const extractAdAccountsFromIntegration = ({
  platform,
  integration,
} = {}) => {
  const linkingPlatform = toAgencyLinkingPlatform(platform);
  const record = unwrapIntegration(integration);
  if (!linkingPlatform || !record) return [];

  const integrationID = String(record.id || record.integrationID || "");
  const advertiserID = record.advertiserID ? String(record.advertiserID) : null;
  const map =
    linkingPlatform === AGENCY_LINKING_PLATFORM.META
      ? record.businessAccounts || record.adAccounts
      : record.advertisers || record.adAccounts || record.businessAccounts;

  return toEntries(map)
    .map((account, index) =>
      normalizeAccount(
        {
          ...account,
          platform: account.platform || linkingPlatform,
          integrationID: account.integrationID || integrationID,
          integrationName: account.integrationName || record.name,
          advertiserID: account.advertiserID || advertiserID,
        },
        index
      )
    )
    .filter(Boolean);
};

const unwrapIntegration = (integration) => {
  const record = readObject(integration);
  if (!record) return null;
  if (record.businessAccounts || record.advertisers || record.adAccounts) {
    return record;
  }
  if (record.data) return unwrapIntegration(record.data);
  if (record.integration) return unwrapIntegration(record.integration);
  return record;
};

const readVariant = (data) => {
  const record = readObject(data) || {};
  // An explicit null variant means "not classified". Do not fall through to
  // mode, which used to default unclassified advertisers to "direct".
  const value = Object.hasOwn(record, "variant") ? record.variant : record.mode;
  if (value === AGENCY_LINKING_VARIANT.DIRECT) {
    return AGENCY_LINKING_VARIANT.DIRECT;
  }
  if (value === AGENCY_LINKING_VARIANT.AGENCY) {
    return AGENCY_LINKING_VARIANT.AGENCY;
  }
  return null;
};

const readCode = (root, data) =>
  root.code || root.errorCode || data.code || null;

const isConsentBlockedCode = (code) =>
  code === AGENCY_LINKING_CODE.CONSENT_REQUIRES_CLIENT ||
  code === AGENCY_LINKING_CODE.CONSENT_REQUIRED;

const normalizeNeedsReauth = (value) =>
  toEntries(value)
    .map((item) => {
      const platform = toAgencyLinkingPlatform(item?.platform);
      const integrationID = String(item?.integrationID || "");
      if (!platform || !integrationID) return null;
      return {
        advertiserID: item?.advertiserID ? String(item.advertiserID) : null,
        platform,
        integrationID,
      };
    })
    .filter(Boolean);

const readAccountLinkKey = (account) => {
  if (account?.linkKey) return account.linkKey;
  const platform = account?.platform;
  const adAccountID = account?.adAccountID || account?.accountID;
  if (platform && adAccountID) {
    return agencyAccountLinkKey({ platform, adAccountID });
  }
  const id = String(account?.id || "");
  const parts = id.split(":");
  if (parts.length >= 2) {
    return `${parts[0]}:${parts[1]}`;
  }
  return id;
};

const dedupeAccounts = (accounts) => {
  const seen = new Set();
  return accounts.filter((account) => {
    if (!account?.id || seen.has(account.id)) return false;
    seen.add(account.id);
    return true;
  });
};

const normalizeTargets = (value) =>
  dedupeAccounts(
    toEntries(value)
      .map((account, index) => {
        const normalized = normalizeAccount(account, index);
        if (!normalized) return null;
        return {
          ...normalized,
          status: normalized.status || AGENCY_LINKING_STATUS.UNLINKED,
        };
      })
      .filter(Boolean)
  );

const splitResultKey = (key) => {
  const value = String(key || "");
  const separator = value.indexOf(":");
  if (separator <= 0) return {};
  return {
    platform: value.slice(0, separator),
    adAccountID: value.slice(separator + 1),
  };
};

const normalizeResultsMap = (results) => {
  if (!results) return [];
  if (Array.isArray(results)) {
    return results
      .map((item, index) => normalizeAccount(item, index))
      .filter(Boolean);
  }
  const record = readObject(results);
  if (!record) return [];
  return Object.entries(record)
    .map(([key, value]) => {
      const fromKey = splitResultKey(key);
      const row = readObject(value) || {};
      return normalizeAccount(
        {
          ...fromKey,
          ...row,
          advertiserID: row.advertiserID || fromKey.advertiserID,
          platform: row.platform || fromKey.platform,
          adAccountID: row.adAccountID || fromKey.adAccountID,
        },
        key
      );
    })
    .filter(Boolean);
};

const countSummary = (results) =>
  results.reduce(
    (summary, item) => {
      summary.total += 1;
      if (item.status === AGENCY_LINKING_STATUS.LINKED) summary.linked += 1;
      else if (item.status === AGENCY_LINKING_STATUS.FAILED)
        summary.failed += 1;
      else summary.pending += 1;
      return summary;
    },
    { total: 0, linked: 0, pending: 0, failed: 0 }
  );

const normalizeSummary = (summary, results) => {
  const record = readObject(summary);
  if (!record) return countSummary(results);
  return {
    total: Number(record.total) || 0,
    linked: Number(record.linked) || 0,
    pending: Number(record.pending) || 0,
    failed: Number(record.failed) || 0,
  };
};

export const emptyAgencyLinkingPrompt = ({ consentBlocked = false } = {}) => ({
  ok: false,
  show: false,
  variant: null,
  showPickerLine: false,
  showFailureBanner: false,
  forcedAgencyMode: false,
  accounts: [],
  targetAccounts: [],
  directFailureAccounts: [],
  availablePlatforms: [],
  needsReauth: [],
  consentBlocked,
});

// Direct advertisers are linked in the background, so they get no prompt and
// no picker. A failed background link sets showFailureBanner so the same
// agency card can explain the error. A missing variant stays null.
export const isAgencyLinkingPromptVisible = (prompt) => {
  if (!prompt || prompt.consentBlocked) return false;
  if (
    prompt.variant === AGENCY_LINKING_VARIANT.DIRECT &&
    prompt.showFailureBanner === true
  ) {
    return prompt.accounts?.length > 0;
  }
  return (
    prompt.show === true && prompt.variant === AGENCY_LINKING_VARIANT.AGENCY
  );
};

// Session hide is passed in. show comes from the prompt as-is.
export const isAgencyLinkingAlertVisible = ({
  enabled = false,
  dismissedForSession = false,
  hidden = false,
  completed = false,
  prompt,
} = {}) => {
  if (!enabled || dismissedForSession || hidden || prompt?.consentBlocked) {
    return false;
  }
  const showFailureBanner = prompt?.showFailureBanner === true;
  if (!showFailureBanner && completed) return false;
  return isAgencyLinkingPromptVisible(prompt);
};

const readShowPickerLine = ({ data, variant }) =>
  variant === AGENCY_LINKING_VARIANT.AGENCY && data?.showPickerLine === true;

export const normalizeAgencyLinkingPrompt = (raw) => {
  const { root, data } = unwrapPayload(raw);
  const code = readCode(root, data);
  if (isConsentBlockedCode(code)) {
    return emptyAgencyLinkingPrompt({ consentBlocked: true });
  }

  const variant = readVariant(data);
  const availablePlatforms = Array.isArray(data.availablePlatforms)
    ? data.availablePlatforms.map((item) => String(item))
    : [];
  const accounts = filterAccountsByAvailablePlatforms({
    accounts: normalizeTargets(data.targets),
    availablePlatforms,
  });
  const showPickerLine = readShowPickerLine({ data, variant });
  const show =
    data.show === true &&
    variant === AGENCY_LINKING_VARIANT.AGENCY &&
    accounts.length > 0;
  const directFailureAccounts =
    variant === AGENCY_LINKING_VARIANT.DIRECT
      ? accounts.filter(
          (account) => account.status === AGENCY_LINKING_STATUS.FAILED
        )
      : [];

  return {
    ok: root.ok !== false,
    show,
    variant,
    showPickerLine,
    showFailureBanner: false,
    forcedAgencyMode: false,
    accounts: show ? accounts : [],
    targetAccounts: accounts,
    directFailureAccounts,
    availablePlatforms,
    needsReauth: normalizeNeedsReauth(data.needsReauth),
    consentBlocked: false,
  };
};

const INTEGRATION_BUCKETS = Object.freeze([
  { bucket: "facebook", platform: AGENCY_LINKING_PLATFORM.META },
  { bucket: "google", platform: AGENCY_LINKING_PLATFORM.GOOGLE },
  { bucket: "tiktokBusiness", platform: AGENCY_LINKING_PLATFORM.TIKTOK },
]);

// Ad accounts already stored on the signed-in user's integrations. Used when
// a direct background link has no prompt rows, and when an AppSumo agency
// was classified as direct so the sheet still has something to show.
export const collectIntegrationAdAccounts = ({
  integrations,
  availablePlatforms = [],
} = {}) => {
  const record = readObject(integrations);
  if (!record) return [];

  const accounts = INTEGRATION_BUCKETS.flatMap(({ bucket, platform }) => {
    const group = readObject(record[bucket]);
    if (!group) return [];
    return Object.values(group).flatMap((integration) =>
      extractAdAccountsFromIntegration({ platform, integration })
    );
  });

  return filterAccountsByAvailablePlatforms({
    accounts,
    availablePlatforms,
  }).map((account) => ({
    ...account,
    status: account.status || AGENCY_LINKING_STATUS.UNLINKED,
  }));
};

// Prompt rows win when they already say failed. Otherwise the integration
// row keeps its own status until the prompt is refreshed.
export const mergeAgencyLinkCandidates = ({
  accounts = [],
  failedAccounts = [],
} = {}) => {
  const byID = new Map(
    accounts
      .filter((account) => account?.id)
      .map((account) => [account.id, account])
  );
  failedAccounts.forEach((account) => {
    if (!account?.id) return;
    const current = byID.get(account.id);
    if (!current) {
      byID.set(account.id, account);
      return;
    }
    if (
      current.status !== AGENCY_LINKING_STATUS.FAILED &&
      account.status === AGENCY_LINKING_STATUS.FAILED
    ) {
      byID.set(account.id, { ...current, ...account });
    }
  });
  return [...byID.values()];
};

export const selectFailedAgencyAccounts = ({ accounts = [] } = {}) =>
  accounts.filter(
    (account) =>
      agencyAccountLinkStatus(account) === AGENCY_LINKING_STATUS.FAILED
  );

const sameAccountRows = (current = [], next = []) =>
  current.length === next.length &&
  current.every(
    (account, index) =>
      account?.id === next[index]?.id &&
      account?.status === next[index]?.status &&
      (account?.errorCode || null) === (next[index]?.errorCode || null)
  );

// AppSumo agencies always use the agency sheet, even when the prompt
// classified the advertiser as direct or left the variant empty.
export const applyAppSumoAgencyMode = ({
  prompt,
  isAppSumo = false,
  integrationAccounts = [],
} = {}) => {
  if (isAppSumo !== true || !prompt || prompt.consentBlocked) return prompt;

  const promptAccounts = prompt.accounts || [];
  const usingIntegrations = promptAccounts.length === 0;
  const source = usingIntegrations ? integrationAccounts : promptAccounts;
  const rows = usingIntegrations
    ? source.filter(
        (account) => account.status !== AGENCY_LINKING_STATUS.LINKED
      )
    : source;
  const show = rows.some(
    (account) => account.status !== AGENCY_LINKING_STATUS.LINKED
  );
  const nextAccounts = show ? rows : [];

  if (
    prompt.forcedAgencyMode === true &&
    prompt.variant === AGENCY_LINKING_VARIANT.AGENCY &&
    prompt.showPickerLine === true &&
    prompt.showFailureBanner !== true &&
    prompt.show === show &&
    sameAccountRows(promptAccounts, nextAccounts)
  ) {
    return prompt;
  }

  return {
    ...prompt,
    variant: AGENCY_LINKING_VARIANT.AGENCY,
    showPickerLine: true,
    showFailureBanner: false,
    forcedAgencyMode: true,
    show,
    accounts: nextAccounts,
  };
};

// Keeps variant "direct" and the picker closed. The agency card appears
// only when at least one background link failed.
export const buildDirectFailureBannerPrompt = ({
  prompt,
  accounts = [],
} = {}) => {
  if (!prompt || prompt.consentBlocked) return prompt;
  if (prompt.variant !== AGENCY_LINKING_VARIANT.DIRECT) return prompt;

  const failed = selectFailedAgencyAccounts({ accounts });
  if (failed.length === 0) {
    if (!prompt.showFailureBanner && !(prompt.accounts?.length > 0)) {
      return prompt;
    }
    return {
      ...prompt,
      show: false,
      showPickerLine: false,
      showFailureBanner: false,
      accounts: [],
    };
  }

  return {
    ...prompt,
    show: false,
    showPickerLine: false,
    showFailureBanner: true,
    accounts: failed,
  };
};

export const buildAgencyLinkingSurface = ({
  prompt,
  isAppSumo = false,
  integrationAccounts = [],
} = {}) => {
  if (!prompt || prompt.consentBlocked) return prompt;

  if (isAppSumo === true || prompt.forcedAgencyMode === true) {
    return applyAppSumoAgencyMode({
      prompt,
      isAppSumo: true,
      integrationAccounts,
    });
  }

  if (prompt.variant !== AGENCY_LINKING_VARIANT.DIRECT) return prompt;

  return buildDirectFailureBannerPrompt({
    prompt,
    accounts: mergeAgencyLinkCandidates({
      accounts: integrationAccounts,
      failedAccounts: prompt.directFailureAccounts,
    }),
  });
};

export const normalizeLinkResponse = (raw) => {
  const { root, data } = unwrapPayload(raw);
  const code = readCode(root, data);
  const results = normalizeResultsMap(data.results);
  const needsReauth = normalizeNeedsReauth(data.needsReauth);

  return {
    ok: root.ok === true,
    code,
    consentBlocked: isConsentBlockedCode(code),
    inProgress: code === AGENCY_LINKING_CODE.IN_PROGRESS,
    notConfigured: code === AGENCY_LINKING_CODE.NOT_CONFIGURED,
    batchID: data.batchID || null,
    summary: normalizeSummary(data.summary, results),
    results,
    needsReauth,
  };
};

// No node means the account is connected and nobody has confirmed.
export const normalizeAgencyAccountLink = (record) => {
  if (record == null) {
    return { present: false, status: null, stage: null, errorCode: null };
  }
  return {
    present: true,
    status: normalizeStatus(record.status),
    stage: normalizeAgencyLinkStage(record.stage),
    errorCode: record.error?.code || record.errorCode || null,
  };
};

export const normalizeAgencyLinkBatch = (record) => {
  if (!record || typeof record !== "object") return null;
  const total = Number(record.total) || 0;
  const done = Number(record.done) || 0;
  const finished = record.finishedAt != null && record.finishedAt !== "";
  return {
    total,
    done,
    linked: Number(record.linked) || 0,
    pending: Number(record.pending) || 0,
    failed: Number(record.failed) || 0,
    finished,
    terminal: finished || (total > 0 && done >= total),
    results: normalizeResultsMap(record.results),
  };
};

export const agencyLinkBatchToastView = (batch) => {
  if (!batch?.terminal) return null;
  return summarizeLinkResults({ summary: batch });
};

export const normalizeAgencyAccountLinks = (raw) => {
  const { data } = unwrapPayload(raw);
  return normalizeResultsMap(data.links);
};

export const readAgencyLinkResumeBatchID = (search = "") => {
  const params = new URLSearchParams(
    String(search).startsWith("?") ? String(search).slice(1) : String(search)
  );
  const value = params.get(AGENCY_LINK_RESUME_QUERY);
  return value ? String(value) : null;
};

export const withAgencyLinkResumeBatchID = ({ pending, search = "" } = {}) => {
  if (!pending) return null;
  const resumeBatchID = readAgencyLinkResumeBatchID(search);
  if (!resumeBatchID) return pending;
  return { ...pending, resumeBatchID };
};

export const summarizeLinkResults = ({ summary } = {}) => {
  const linked = Number(summary?.linked) || 0;
  const failed = Number(summary?.failed) || 0;
  const pending = Number(summary?.pending) || 0;

  if (linked > 0 && failed === 0) {
    return { kind: "done", count: linked };
  }
  if (linked > 0 && failed > 0) {
    return { kind: "partial", linked, total: linked + failed };
  }
  if (failed > 0 && pending === 0) {
    return { kind: "later", count: failed };
  }
  return { kind: "silent" };
};

export const AGENCY_SHEET_LINK_OUTCOME = Object.freeze({
  SUCCESS: "success",
  ISSUE: "issue",
});

export const AGENCY_SHEET_GUIDANCE = Object.freeze({
  APPROVE: "approve",
  NOT_CONFIGURED: "notConfigured",
  RECONNECT: "reconnect",
  REQUEST_AGAIN: "requestAgain",
  SUPPORT: "support",
  RETRY: "retry",
  IN_PROGRESS: "inProgress",
  PROCESSING: "processing",
});

// Per-account errorCode values returned by linkAdAccountsToAgency in
// results[`${platform}:${adAccountID}`].errorCode (backend
// functions/src/utils/errors.js). Pending approval has no code of its own;
// it is the awaitingClientApproval stage.
export const AGENCY_LINK_RESULT_CODE = Object.freeze({
  NOT_CONFIGURED: "agencyLinkingNotConfigured",
  PLATFORM_NOT_SUPPORTED: "agencyLinkingPlatformNotSupported",
  MISSING_PERMISSION: "integrationMissingPermission",
  INTEGRATION_NOT_FOUND: "integrationInformationNotFound",
  INSUFFICIENT_ACCESS: "agencyLinkInsufficientAccess",
  ACCOUNT_NOT_IN_BUSINESS: "agencyLinkAccountNotInBusiness",
  LIMIT_REACHED: "agencyLinkLimitReached",
  ALREADY_MANAGED_ELSEWHERE: "agencyLinkAlreadyManagedElsewhere",
  INCOMPATIBLE_ACCOUNTS: "agencyLinkIncompatibleAccounts",
  REJECTED: "agencyLinkRejected",
  EXPIRED: "agencyLinkExpired",
  REVOKED: "agencyLinkRevoked",
  PLATFORM_ERROR: "agencyLinkPlatformError",
});

const GUIDANCE_BY_CODE = Object.freeze({
  [AGENCY_LINK_RESULT_CODE.NOT_CONFIGURED]:
    AGENCY_SHEET_GUIDANCE.NOT_CONFIGURED,
  [AGENCY_LINK_RESULT_CODE.MISSING_PERMISSION]: AGENCY_SHEET_GUIDANCE.RECONNECT,
  [AGENCY_LINK_RESULT_CODE.INTEGRATION_NOT_FOUND]:
    AGENCY_SHEET_GUIDANCE.RECONNECT,
  [AGENCY_LINK_RESULT_CODE.INSUFFICIENT_ACCESS]:
    AGENCY_SHEET_GUIDANCE.RECONNECT,
  [AGENCY_LINK_RESULT_CODE.REJECTED]: AGENCY_SHEET_GUIDANCE.REQUEST_AGAIN,
  [AGENCY_LINK_RESULT_CODE.EXPIRED]: AGENCY_SHEET_GUIDANCE.REQUEST_AGAIN,
  [AGENCY_LINK_RESULT_CODE.REVOKED]: AGENCY_SHEET_GUIDANCE.REQUEST_AGAIN,
  [AGENCY_LINK_RESULT_CODE.ACCOUNT_NOT_IN_BUSINESS]:
    AGENCY_SHEET_GUIDANCE.SUPPORT,
  [AGENCY_LINK_RESULT_CODE.LIMIT_REACHED]: AGENCY_SHEET_GUIDANCE.SUPPORT,
  [AGENCY_LINK_RESULT_CODE.ALREADY_MANAGED_ELSEWHERE]:
    AGENCY_SHEET_GUIDANCE.SUPPORT,
  [AGENCY_LINK_RESULT_CODE.INCOMPATIBLE_ACCOUNTS]:
    AGENCY_SHEET_GUIDANCE.SUPPORT,
  [AGENCY_LINK_RESULT_CODE.PLATFORM_NOT_SUPPORTED]:
    AGENCY_SHEET_GUIDANCE.SUPPORT,
});

// Pending rows that are only queued, linking or awaiting the TikTok partner
// step need no action and return null (the sheet says it is processing).
const guidanceForResult = (result) => {
  if (!result || result.status === AGENCY_LINKING_STATUS.LINKED) return null;
  if (result.stage === AGENCY_LINKING_STAGE.AWAITING_CLIENT_APPROVAL) {
    return AGENCY_SHEET_GUIDANCE.APPROVE;
  }
  if (result.status !== AGENCY_LINKING_STATUS.FAILED) return null;
  return GUIDANCE_BY_CODE[result.errorCode] || AGENCY_SHEET_GUIDANCE.RETRY;
};

const matchSubmittedAccount = ({ result, accounts }) =>
  accounts.find((account) => {
    const accountID = account.adAccountID || account.accountID;
    if (account.platform !== result.platform) return false;
    if (accountID !== result.adAccountID) return false;
    if (
      result.integrationID &&
      account.integrationID &&
      account.integrationID !== result.integrationID
    ) {
      return false;
    }
    if (
      result.advertiserID &&
      account.advertiserID &&
      account.advertiserID !== result.advertiserID
    ) {
      return false;
    }
    return true;
  }) || null;

const collectSheetIssues = ({ results = [], accounts = [] }) =>
  results
    .map((result) => {
      const guidance = guidanceForResult(result);
      if (!guidance) return null;
      const submitted = matchSubmittedAccount({ result, accounts });
      return {
        id: submitted?.id || result.id,
        name: submitted?.name || result.name || result.adAccountID,
        platform: result.platform,
        errorCode: result.errorCode || null,
        guidance,
      };
    })
    .filter(Boolean);

const emptySheetFeedback = ({
  outcome = AGENCY_SHEET_LINK_OUTCOME.ISSUE,
  sheetGuidance = AGENCY_SHEET_GUIDANCE.RETRY,
  linkedCount = 0,
  issues = [],
} = {}) => ({
  outcome,
  sheetGuidance,
  linkedCount,
  issues,
});

// The agency sheet stays open unless every selected account linked without
// a per-account error. Pending approval (Meta requires_admin_approval) and
// failed rows keep the sheet open so the user sees the next step.
export const buildAgencySheetLinkFeedback = ({
  response,
  accounts = [],
} = {}) => {
  if (!response) {
    return emptySheetFeedback();
  }
  if (response.inProgress) {
    return emptySheetFeedback({
      sheetGuidance: AGENCY_SHEET_GUIDANCE.IN_PROGRESS,
    });
  }
  // A request-level notConfigured means a platform outside
  // availablePlatforms; the prompt is refreshed, so retrying is the fix.
  if (response.notConfigured || response.skipped) {
    return emptySheetFeedback();
  }

  const linkedCount = Number(response.summary?.linked) || 0;
  const failedCount = Number(response.summary?.failed) || 0;
  const pendingCount = Number(response.summary?.pending) || 0;
  const issues = collectSheetIssues({
    results: response.results,
    accounts,
  });

  if (issues.length > 0) {
    return emptySheetFeedback({
      sheetGuidance: null,
      linkedCount,
      issues,
    });
  }

  if (linkedCount > 0 && failedCount === 0) {
    return emptySheetFeedback({
      outcome: AGENCY_SHEET_LINK_OUTCOME.SUCCESS,
      sheetGuidance: null,
      linkedCount,
    });
  }

  if (pendingCount > 0 && failedCount === 0) {
    return emptySheetFeedback({
      sheetGuidance: AGENCY_SHEET_GUIDANCE.PROCESSING,
      linkedCount,
    });
  }

  return emptySheetFeedback({ linkedCount });
};

const approvalTextKey = (platform) => {
  switch (platform) {
    case AGENCY_LINKING_PLATFORM.META:
      return AGENCY_ACCOUNT_LINKING_SHEET_APPROVE_META;
    case AGENCY_LINKING_PLATFORM.GOOGLE:
      return AGENCY_ACCOUNT_LINKING_SHEET_APPROVE_GOOGLE;
    case AGENCY_LINKING_PLATFORM.TIKTOK:
      return AGENCY_ACCOUNT_LINKING_SHEET_APPROVE_TIKTOK;
    default: {
      const _unexpected = platform;
      void _unexpected;
      return AGENCY_ACCOUNT_LINKING_SHEET_APPROVE_GENERIC;
    }
  }
};

const issueTextKey = (issue) => {
  switch (issue.guidance) {
    case AGENCY_SHEET_GUIDANCE.APPROVE:
      return approvalTextKey(issue.platform);
    case AGENCY_SHEET_GUIDANCE.NOT_CONFIGURED:
      return AGENCY_ACCOUNT_LINKING_SHEET_NOT_CONFIGURED;
    case AGENCY_SHEET_GUIDANCE.RECONNECT:
      return AGENCY_ACCOUNT_LINKING_SHEET_RECONNECT;
    case AGENCY_SHEET_GUIDANCE.REQUEST_AGAIN:
      return AGENCY_ACCOUNT_LINKING_SHEET_REQUEST_AGAIN;
    case AGENCY_SHEET_GUIDANCE.SUPPORT:
      return AGENCY_ACCOUNT_LINKING_SHEET_SUPPORT;
    case AGENCY_SHEET_GUIDANCE.RETRY:
      return AGENCY_ACCOUNT_LINKING_SHEET_RETRY_STEP;
    default: {
      const _unexpected = issue.guidance;
      void _unexpected;
      return AGENCY_ACCOUNT_LINKING_SHEET_RETRY_STEP;
    }
  }
};

const uniformIssueTextKey = (issues) => {
  if (issues.length === 0) return null;
  const first = issueTextKey(issues[0]);
  return issues.every((issue) => issueTextKey(issue) === first) ? first : null;
};

const toFeedbackLines = ({ issues, includeStep }) =>
  issues.map((issue) => ({
    id: issue.id,
    name: issue.name,
    textKey: includeStep ? issueTextKey(issue) : null,
  }));

export const describeAgencySheetLinkFeedback = (feedback) => {
  if (!feedback || feedback.outcome !== AGENCY_SHEET_LINK_OUTCOME.ISSUE) {
    return null;
  }

  switch (feedback.sheetGuidance) {
    case AGENCY_SHEET_GUIDANCE.IN_PROGRESS:
      return {
        titleKey: AGENCY_ACCOUNT_LINKING_SHEET_IN_PROGRESS_TITLE,
        textKey: AGENCY_ACCOUNT_LINKING_SHEET_IN_PROGRESS,
        lines: [],
      };
    case AGENCY_SHEET_GUIDANCE.PROCESSING:
      return {
        titleKey: AGENCY_ACCOUNT_LINKING_SHEET_PROCESSING_TITLE,
        textKey: AGENCY_ACCOUNT_LINKING_SHEET_PROCESSING,
        lines: [],
      };
    case AGENCY_SHEET_GUIDANCE.RETRY:
      return {
        titleKey: AGENCY_ACCOUNT_LINKING_SHEET_FAILED_TITLE,
        textKey: AGENCY_ACCOUNT_LINKING_TOAST_LATER,
        lines: [],
      };
    default:
      break;
  }

  const issues = feedback.issues || [];
  const linked = Number(feedback.linkedCount) || 0;
  const uniformKey = uniformIssueTextKey(issues);

  if (linked > 0) {
    return {
      titleKey: AGENCY_ACCOUNT_LINKING_SHEET_PARTIAL_TITLE,
      textKey: AGENCY_ACCOUNT_LINKING_SHEET_PARTIAL_BODY,
      textOptions: { linked, total: linked + issues.length },
      lines: toFeedbackLines({ issues, includeStep: true }),
    };
  }

  if (
    issues.every((issue) => issue.guidance === AGENCY_SHEET_GUIDANCE.APPROVE)
  ) {
    return {
      titleKey: AGENCY_ACCOUNT_LINKING_SHEET_APPROVAL_TITLE,
      textKey: uniformKey || AGENCY_ACCOUNT_LINKING_SHEET_APPROVAL_BODY,
      lines: toFeedbackLines({ issues, includeStep: !uniformKey }),
    };
  }

  return {
    titleKey: AGENCY_ACCOUNT_LINKING_SHEET_FAILED_TITLE,
    textKey:
      uniformKey === AGENCY_ACCOUNT_LINKING_SHEET_RETRY_STEP
        ? AGENCY_ACCOUNT_LINKING_TOAST_LATER
        : uniformKey || AGENCY_ACCOUNT_LINKING_SHEET_FAILED_BODY,
    lines: toFeedbackLines({ issues, includeStep: !uniformKey }),
  };
};

export const toAgencyLinkAccount = (account, availablePlatforms = []) => {
  const payload = {
    platform: platformWireValue(account.platform, availablePlatforms),
    integrationID: account.integrationID,
    adAccountID: String(account.adAccountID || account.accountID || ""),
  };
  if (account.advertiserID) payload.advertiserID = account.advertiserID;
  return payload;
};

export const buildLinkRequest = ({
  source,
  locale,
  accounts = [],
  resumeBatchID,
  availablePlatforms = [],
} = {}) => {
  const body = {
    source,
    locale: toAgencyLinkingLocale(locale),
    accounts: filterAccountsByAvailablePlatforms({
      accounts,
      availablePlatforms,
    }).map((account) => toAgencyLinkAccount(account, availablePlatforms)),
  };
  // A resumed batch already stored the copy. A new submit still sends it.
  if (resumeBatchID) body.resumeBatchID = resumeBatchID;
  else body.copyVersion = AGENCY_LINKING_COPY_VERSION;
  return body;
};

export const agencyAccountLinkStatus = (account) => {
  const status = account?.status;
  if (
    status === AGENCY_LINKING_STATUS.PENDING ||
    status === AGENCY_LINKING_STATUS.FAILED ||
    status === AGENCY_LINKING_STATUS.LINKED ||
    status === AGENCY_LINKING_STATUS.UNLINKED
  ) {
    return status;
  }
  return AGENCY_LINKING_STATUS.UNLINKED;
};

export const isAgencyAccountSelectable = (account) => {
  const status = agencyAccountLinkStatus(account);
  return (
    status === AGENCY_LINKING_STATUS.UNLINKED ||
    status === AGENCY_LINKING_STATUS.FAILED
  );
};

export const mergeAccountLinkSnapshot = (account, link) => {
  if (!link?.present || !link.status) return account;
  return {
    ...account,
    status: link.status,
    stage: link.stage ?? null,
    errorCode: link.errorCode || null,
  };
};

export const excludeLinkedAgencyAccounts = (accounts = []) =>
  accounts.filter(
    (account) =>
      agencyAccountLinkStatus(account) !== AGENCY_LINKING_STATUS.LINKED
  );

export const applyPromptLinkStatusToAccounts = ({
  accounts = [],
  promptTargets = [],
} = {}) => {
  const statusByLinkKey = new Map();
  promptTargets.forEach((target) => {
    const platform = toAgencyLinkingPlatform(target?.platform);
    const adAccountID = String(target?.adAccountID || target?.accountID || "");
    if (!platform || !adAccountID) return;
    statusByLinkKey.set(
      agencyAccountLinkKey({ platform, adAccountID }),
      agencyAccountLinkStatus(target)
    );
  });

  return accounts.map((account) => {
    const platform = toAgencyLinkingPlatform(account?.platform);
    const adAccountID = String(
      account?.adAccountID || account?.accountID || ""
    );
    if (!platform || !adAccountID) return account;
    const status = statusByLinkKey.get(
      agencyAccountLinkKey({ platform, adAccountID })
    );
    if (!status) return account;
    return { ...account, status };
  });
};

export const agencyAccountLinkStatusKey = (account) => {
  switch (agencyAccountLinkStatus(account)) {
    case AGENCY_LINKING_STATUS.PENDING:
      return AGENCY_ACCOUNT_LINKING_STATUS_PENDING;
    case AGENCY_LINKING_STATUS.FAILED:
      return AGENCY_ACCOUNT_LINKING_STATUS_FAILED;
    case AGENCY_LINKING_STATUS.LINKED:
      return AGENCY_ACCOUNT_LINKING_STATUS_LINKED;
    case AGENCY_LINKING_STATUS.UNLINKED:
      return AGENCY_ACCOUNT_LINKING_STATUS_UNLINKED;
    default: {
      const _unexpected = agencyAccountLinkStatus(account);
      void _unexpected;
      return AGENCY_ACCOUNT_LINKING_STATUS_UNLINKED;
    }
  }
};

const DETAIL_KEY_BY_CODE = Object.freeze({
  [AGENCY_LINK_RESULT_CODE.INSUFFICIENT_ACCESS]:
    AGENCY_ACCOUNT_LINKING_DETAIL_NOT_ADMIN,
  [AGENCY_LINK_RESULT_CODE.MISSING_PERMISSION]:
    AGENCY_ACCOUNT_LINKING_DETAIL_RECONNECT,
  [AGENCY_LINK_RESULT_CODE.INTEGRATION_NOT_FOUND]:
    AGENCY_ACCOUNT_LINKING_DETAIL_UNREADABLE,
  [AGENCY_LINK_RESULT_CODE.NOT_CONFIGURED]:
    AGENCY_ACCOUNT_LINKING_DETAIL_NOT_CONFIGURED,
  [AGENCY_LINK_RESULT_CODE.ACCOUNT_NOT_IN_BUSINESS]:
    AGENCY_ACCOUNT_LINKING_DETAIL_NOT_IN_BUSINESS,
  [AGENCY_LINK_RESULT_CODE.LIMIT_REACHED]: AGENCY_ACCOUNT_LINKING_DETAIL_LIMIT,
  [AGENCY_LINK_RESULT_CODE.ALREADY_MANAGED_ELSEWHERE]:
    AGENCY_ACCOUNT_LINKING_DETAIL_MANAGED_ELSEWHERE,
  [AGENCY_LINK_RESULT_CODE.INCOMPATIBLE_ACCOUNTS]:
    AGENCY_ACCOUNT_LINKING_DETAIL_INCOMPATIBLE,
  [AGENCY_LINK_RESULT_CODE.REJECTED]: AGENCY_ACCOUNT_LINKING_DETAIL_REJECTED,
  [AGENCY_LINK_RESULT_CODE.EXPIRED]: AGENCY_ACCOUNT_LINKING_DETAIL_EXPIRED,
  [AGENCY_LINK_RESULT_CODE.REVOKED]: AGENCY_ACCOUNT_LINKING_DETAIL_REVOKED,
  [AGENCY_LINK_RESULT_CODE.PLATFORM_NOT_SUPPORTED]:
    AGENCY_ACCOUNT_LINKING_DETAIL_UNSUPPORTED,
  [AGENCY_LINK_RESULT_CODE.PLATFORM_ERROR]:
    AGENCY_ACCOUNT_LINKING_DETAIL_GENERIC,
});

export const agencyAccountLinkDetailKey = (account) => {
  const status = agencyAccountLinkStatus(account);
  const code = account?.errorCode;
  if (!code) return null;
  if (
    status !== AGENCY_LINKING_STATUS.FAILED &&
    status !== AGENCY_LINKING_STATUS.PENDING
  ) {
    return null;
  }
  return DETAIL_KEY_BY_CODE[code] || AGENCY_ACCOUNT_LINKING_DETAIL_GENERIC;
};

// A cold boot answers `{ ok: true }` with no variant and no platforms. That
// must not replace a prompt that already has rows, or the sheet flashes back
// to "unlinked" until the real request returns.
export const shouldReplaceAgencyLinkingPrompt = (current, next) => {
  if (!current?.accounts?.length) return true;
  if (!next) return false;
  return next.variant != null || next.availablePlatforms?.length > 0;
};

export const applyLinkResultsToAccounts = ({
  accounts = [],
  results = [],
} = {}) => {
  const byLinkKey = new Map(
    results.map((result) => [readAccountLinkKey(result), result])
  );
  return accounts.map((account) => {
    const result = byLinkKey.get(readAccountLinkKey(account));
    if (!result?.status) return account;
    const clearError =
      result.status === AGENCY_LINKING_STATUS.LINKED ||
      result.status === AGENCY_LINKING_STATUS.UNLINKED;
    return {
      ...account,
      status: result.status,
      stage: result.stage ?? null,
      errorCode: clearError ? null : result.errorCode || null,
    };
  });
};

export const groupAccountsByIntegration = (accounts = []) => {
  const groups = [];
  const indexByIntegration = new Map();

  accounts.forEach((account) => {
    const groupKey = `${account.platform}:${account.integrationID}`;
    const existingIndex = indexByIntegration.get(groupKey);
    if (existingIndex === undefined) {
      indexByIntegration.set(groupKey, groups.length);
      groups.push({
        groupKey,
        platform: account.platform,
        integrationName: account.integrationName || account.integrationID,
        accounts: [account],
      });
      return;
    }
    groups[existingIndex].accounts.push(account);
  });

  return groups;
};

export const selectableAgencyAccountIds = (accounts = []) => {
  const seen = new Set();
  const ids = [];
  groupAccountsByIntegration(accounts)
    .flatMap((group) => group.accounts)
    .forEach((account) => {
      if (!isAgencyAccountSelectable(account) || seen.has(account.id)) return;
      seen.add(account.id);
      ids.push(account.id);
    });
  return ids;
};

const getStorage = (storage) => {
  if (storage) return storage;
  if (typeof window === "undefined") return null;
  return window.localStorage;
};

export const markAgencyLinkingPickerShown = ({
  integrationID,
  now = Date.now(),
  storage,
} = {}) => {
  if (!integrationID) return true;
  const store = getStorage(storage);
  if (!store) return true;
  const key = `${PICKER_SHOWN_PREFIX}${integrationID}`;
  try {
    const raw = store.getItem(key);
    const previous = raw == null || raw === "" ? null : Number(raw);
    if (
      previous != null &&
      Number.isFinite(previous) &&
      now - previous < PICKER_DEDUPE_MS
    ) {
      return false;
    }
    store.setItem(key, String(now));
    return true;
  } catch {
    return true;
  }
};
